import { useNavigate } from 'react-router-dom'

import { AlertTriangle, BarChart3 } from 'lucide-react'

import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import { Button } from '@/common/ui/button'
import { GetChannelTableData } from '@/modules/manage-player/type'
import GetChannelTable from '../GetChannelTable'
import CreateChannelFormModal from '../CreateChannelFormModal'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { PermissionKeys } from '@/modules/manage-member/mappers'

interface GetChannelListingProps {
	data: GetChannelTableData[]
	total: number
	isLoading?: boolean
}

export default function GetChannelListing({
	data,
	total,
	isLoading,
}: GetChannelListingProps) {
	const navigate = useNavigate()
	const { data: user } = useQueryProfile()
	const hasWritePermission = user?.userGroup.writePermission.includes(
		PermissionKeys.MANAGE_PLAYER,
	)

	return (
		<div className="space-y-4">
			<div className="flex items-start justify-between">
				<Heading
					title={`ตั้งค่าช่องทางที่รู้จัก`}
					description="ตั้งค่าช่องทางที่รู้จัก"
				/>
				<div className="flex items-center gap-2">
					<Button
						onClick={() => navigate('/operator/channel-analytic')}
						variant="outline"
						className="flex items-center gap-2"
					>
						<BarChart3 className="h-4 w-4" />
						ดูข้อมูลวิเคราะห์
					</Button>
					{hasWritePermission && <CreateChannelFormModal />}
				</div>
			</div>
			<Separator />
			<div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
				<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
				<div className="text-amber-800 dark:text-amber-200">
					<p className="font-medium">ข้อควรระวังในการตั้งชื่อช่องทาง</p>
					<ul className="mt-0.5 list-disc space-y-0.5 pl-4">
						<li>
							<span className="font-medium">
								ชื่อช่องทางจะถูกใช้เป็น utm_source
							</span>{' '}
							ในลิงก์ติดตามทันทีที่สร้าง เช่น{' '}
							<code className="rounded bg-amber-100 px-1 font-mono text-xs dark:bg-amber-900/50">
								?utm_source=ชื่อช่องทาง
							</code>
						</li>
						<li>
							<span className="font-medium">ลิงก์ติดตามไม่สามารถแก้ไขได้</span>{' '}
							โดย utm_source จะตรงกับชื่อที่กรอก ณ ตอนสร้างเท่านั้น
						</li>
						<li>
							หากต้องการเปลี่ยน utm_source{' '}
							<span className="font-medium">
								ให้สร้างช่องทางใหม่พร้อมชื่อที่ต้องการ
							</span>{' '}
							แล้วปิดการใช้งานช่องทางเดิม
						</li>
						<li>
							แนะนำให้ใช้ตัวอักษร ตัวเลข ขีดกลาง (-) หรือขีดล่าง (_) เช่น{' '}
							<code className="rounded bg-amber-100 px-1 font-mono text-xs dark:bg-amber-900/50">
								facebook-ads
							</code>
							,{' '}
							<code className="rounded bg-amber-100 px-1 font-mono text-xs dark:bg-amber-900/50">
								line_oa
							</code>
						</li>
					</ul>
				</div>
			</div>
			<GetChannelTable data={data} total={total} isLoading={isLoading} />
		</div>
	)
}
